import { useEffect, useRef } from "react";

type Target = number | "add" | null;
const FOCUSABLE = "select, input, textarea, button";

/**
 * Keyboard focus for editable row lists (conditions, actions). Rows are the container's direct
 * children marked `data-row`. After a remove, focus goes to the next row, else the previous one,
 * else the Add button; after an add, to the new row's first control.
 */
export function useRowFocus() {
  const containerRef = useRef<HTMLDivElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const pending = useRef<Target>(null);

  // Runs after every commit: the list change and the focus request land in the same render.
  useEffect(() => {
    const target = pending.current;
    if (target === null) return;
    pending.current = null;
    if (target === "add") {
      addRef.current?.focus();
      return;
    }
    const rows = containerRef.current
      ? containerRef.current.querySelectorAll<HTMLElement>(
          ":scope > [data-row]",
        )
      : [];
    rows[target]?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  });

  /** Call with the removed index and the number of rows left after the removal. */
  const afterRemove = (index: number, remaining: number) => {
    pending.current =
      remaining === 0 ? "add" : index < remaining ? index : remaining - 1;
  };
  const afterAdd = (index: number) => {
    pending.current = index;
  };
  return { containerRef, addRef, afterRemove, afterAdd };
}
